/**
 * roadmap.ts — typed wrappers for the SSI-only roadmap API (/api/roadmap).
 * Server contract: server/src/features/roadmap/ (plain V3 JSON, camelCase).
 */

import { apiDelete, apiGet, apiPatch, apiPost, apiPostMultipart } from '../client';

export const FEATURE_REQUEST_STATUSES = [
  'new',
  'triaged',
  'planning',
  'development',
  'qa',
  'acceptance_testing',
  'ready_for_release',
  'complete',
  'blocked',
  'waiting_on_submitter',
  'declined',
  'duplicate',
] as const;
export type FeatureRequestStatus = (typeof FEATURE_REQUEST_STATUSES)[number];

/** Why a ticket is Blocked (R5.18) — required whenever status enters blocked. */
export const FEATURE_REQUEST_BLOCKED_REASONS = [
  'qa',
  'dependency',
  'third_party',
  'cannot_replicate',
] as const;
export type FeatureRequestBlockedReason = (typeof FEATURE_REQUEST_BLOCKED_REASONS)[number];

export const BLOCKED_REASON_LABELS: Record<FeatureRequestBlockedReason, string> = {
  qa: 'Blocked in QA',
  dependency: 'Behind another ticket or release',
  third_party: 'Waiting on a third party',
  cannot_replicate: 'Cannot replicate',
};

/** Board uses the same status vocabulary as the ticket. */
export const ROADMAP_FEATURE_STATUSES = FEATURE_REQUEST_STATUSES;
export type RoadmapFeatureStatus = FeatureRequestStatus;

export const ROADMAP_EFFORTS = ['easy', 'easy-medium', 'medium', 'medium-hard', 'hard'] as const;
export type RoadmapEffort = (typeof ROADMAP_EFFORTS)[number];

/**
 * What kind of work a ticket represents. Triage reads each differently: a bug
 * is broken shipped behavior, a feature is net-new scope, feedback is an
 * opinion that may become either, and maintenance is upkeep of something
 * already shipped. `''` means nobody has classified it yet.
 */
export const FEATURE_REQUEST_TYPES = ['feature', 'bug', 'feedback', 'maintenance'] as const;
export type FeatureRequestType = (typeof FEATURE_REQUEST_TYPES)[number];

/** Which product a ticket, sprint, or milestone belongs to. */
export const TICKET_ORIGINS = ['tools', 'crm', 'truspace', 'cad'] as const;
export type TicketOrigin = (typeof TICKET_ORIGINS)[number];

export const TICKET_ORIGIN_LABELS: Record<TicketOrigin, string> = {
  tools: 'Tools',
  crm: 'CRM',
  truspace: 'TruSpace',
  cad: 'CAD',
};

/** Triage urgency. `''` means nobody has ranked it yet. */
export const FEATURE_REQUEST_PRIORITIES = ['now', 'next', 'later', 'someday'] as const;
export type FeatureRequestPriority = (typeof FEATURE_REQUEST_PRIORITIES)[number];

/** Client-impact tiers — submitters set this, not priority (R5.13). */
export const FEATURE_REQUEST_IMPACTS = ['low', 'medium-low', 'medium', 'high', 'critical'] as const;
export type FeatureRequestImpact = (typeof FEATURE_REQUEST_IMPACTS)[number];

export const ROADMAP_MILESTONE_STATUSES = [
  'planned',
  'needs-grooming',
  'on-track',
  'at-risk',
  'done',
  'slipped',
] as const;
export type RoadmapMilestoneStatus = (typeof ROADMAP_MILESTONE_STATUSES)[number];

export const MILESTONE_STATUS_LABELS: Record<RoadmapMilestoneStatus, string> = {
  planned: 'Planned',
  'needs-grooming': 'Needs grooming',
  'on-track': 'On track',
  'at-risk': 'At risk',
  done: 'Done',
  slipped: 'Slipped',
};

/** How urgent a milestone is. `''` means nobody has ranked it. */
export const ROADMAP_MILESTONE_PRIORITIES = ['critical', 'high', 'medium', 'low'] as const;
export type RoadmapMilestonePriority = (typeof ROADMAP_MILESTONE_PRIORITIES)[number];

export const MILESTONE_PRIORITY_LABELS: Record<RoadmapMilestonePriority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export type RoadmapCommentEntityType = 'feature' | 'request' | 'milestone';
/** Same vocabulary as comments — images hang off features, requests, or milestones. */
export type RoadmapAttachmentEntityType = RoadmapCommentEntityType;

export interface RoadmapFeature {
  id: number;
  slug: string;
  groupName: string;
  module: string;
  name: string;
  description: string;
  effort: RoadmapEffort | '';
  status: FeatureRequestStatus;
  percentDone: number | null;
  /** Upvote points — a super vote adds more than 1. */
  voteCount: number;
  /** How many of those votes were super votes. */
  superVoteCount?: number;
  createdByName: string;
  createdByEmail: string;
  createdByUserId: number | null;
  requestedBy: string;
  source: string;
  targetDate: string | null;
  deliveredDate: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  /** First tracker step — name/status/effort/progress on the card come from this ticket. */
  linkedRequestId: number | null;
  linkedFrNumber: number | null;
  /** Every ticket on this feature, in tracker step order (`epicSortOrder`). */
  linkedRequests: RoadmapFeatureStep[];
}

/** One step on a feature's progress tracker. */
export interface RoadmapFeatureStep {
  requestId: number;
  frNumber: number;
  name: string;
  status: FeatureRequestStatus;
  epicSortOrder: number;
}

export interface CreateFeatureTicketInput {
  name: string;
  description?: string;
  /** 0-based step position; omitted = append at the end. */
  epicSortOrder?: number;
  origin: TicketOrigin;
}

export interface AttachFeatureTicketInput {
  requestId: number;
  /** 0-based step position; omitted = append at the end. */
  epicSortOrder?: number;
}

/** The live sprint a milestone ticket is committed to (unresolved sprint item). */
export interface RoadmapMilestoneTicketSprint {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
}

/**
 * One ticket committed to a milestone. Structurally a tracker step
 * (`RoadmapFeatureStep`), so the same progress tracker renders it.
 */
export interface RoadmapMilestoneTicket extends RoadmapFeatureStep {
  ticketType: FeatureRequestType | '';
  origin: TicketOrigin;
  priority: string;
  goalDate: string | null;
  promotedToFeatureId: number | null;
  /** Board feature label when the ticket is also a step on a feature. */
  featureName: string | null;
  ownerUserId: number | null;
  owner: string;
  /** Position on the milestone (drag order in the panel). */
  milestoneSortOrder: number;
  sprint: RoadmapMilestoneTicketSprint | null;
}

/**
 * Floor for a milestone executive summary. Matches
 * `EXECUTIVE_SUMMARY_MIN` in server roadmap.schema.ts.
 */
export const EXECUTIVE_SUMMARY_MIN = 80;

/** An engineer assigned to a milestone. The timeline uses one swimlane per person. */
export interface RoadmapMilestoneAssignee {
  userId: number;
  displayName: string;
}

export interface RoadmapMilestone {
  id: number;
  slug: string;
  name: string;
  /** Start of the bar. */
  date: string | null;
  /** End of the bar. Null draws a one-day marker on `date`. */
  closeDate: string | null;
  status: RoadmapMilestoneStatus;
  /** '' = unranked. Colors the timeline bar. */
  priority: RoadmapMilestonePriority | '';
  origin: TicketOrigin;
  notes: string;
  /** What success looks like, and the high-level work. Empty only on older rows. */
  executiveSummary: string;
  sortOrder: number;
  /** Engineers whose swimlane includes this milestone. */
  assignees: RoadmapMilestoneAssignee[];
  /** Every live ticket committed to this milestone, oldest FR first. */
  tickets: RoadmapMilestoneTicket[];
}

export interface CreateMilestoneTicketInput {
  name: string;
  description?: string;
  ticketType?: FeatureRequestType;
  origin: TicketOrigin;
}

export interface AttachMilestoneTicketInput {
  requestId: number;
}

export interface CreateMilestoneInput {
  name: string;
  date?: string | null;
  closeDate?: string | null;
  status?: RoadmapMilestoneStatus;
  priority?: RoadmapMilestonePriority | '';
  assigneeUserIds?: number[];
  origin: TicketOrigin;
  notes?: string;
  /** Required. What success looks like, and the high-level work. */
  executiveSummary: string;
}

export type UpdateMilestoneInput = Partial<CreateMilestoneInput>;

/**
 * How the ticket entered the backlog. 'ui' and 'agent' always carry a
 * submitter; 'seed' marks the rows imported from the docs backlog, which have
 * none (the server enforces that split).
 */
export type FeatureRequestCreatedVia = 'ui' | 'agent' | 'seed';

/** WS2 (R5.2): top level of the two-level category taxonomy. */
export interface TicketArea {
  id: number;
  name: string;
  sort: number;
  active: boolean;
  replacedByAreaId: number | null;
}

/**
 * WS2 (R5.4/R5.7): the specific thing the submitter was looking at. A non-null
 * `secondAreaId` marks a two-home sub-area — the picker must ask which area.
 */
export interface TicketSubarea {
  id: number;
  areaId: number;
  secondAreaId: number | null;
  name: string;
  hint: string;
  sort: number;
  active: boolean;
  replacedBySubareaId: number | null;
}

/** WS2 (R5.6): cross-cutting quality tags — never an area or sub-area. */
export interface TicketTag {
  id: number;
  name: string;
  sort: number;
  active: boolean;
}

export interface RoadmapTaxonomy {
  areas: TicketArea[];
  subareas: TicketSubarea[];
  tags: TicketTag[];
}

export interface FeatureRequest {
  id: number;
  frNumber: number;
  name: string;
  description: string;
  /** feature | bug | feedback | maintenance; '' until triage (or intake) classifies it. */
  ticketType: FeatureRequestType | '';
  /** tools | crm | truspace. Not `source` (who asked). */
  origin: TicketOrigin;
  source: string;
  requestedBy: string;
  dateLogged: string | null;
  category: string;
  /** WS2 structured category — null while unmigrated or on an Other (R5.8). */
  areaId: number | null;
  subareaId: number | null;
  /** The one-line note from an accepted Other (R5.8). */
  categoryOtherNote: string;
  /** Cross-cutting ticket_tags ids (R5.6). */
  tagIds: number[];
  priority: string;
  effortShirt: string;
  effort: RoadmapEffort | '';
  clientImpact: string;
  /** How much billed work or contract money is at stake. Empty until set. */
  revenueImpact: string;
  goalDate: string | null;
  rank: number | null;
  /** Upvote points — a super vote adds more than 1. */
  voteCount: number;
  /** How many of those votes were super votes. */
  superVoteCount?: number;
  /** Times this ticket was carried sprint-to-sprint (WS4, R2.5). */
  carryCount: number;
  /** Left out of the ops averages — something outside the work skewed its timings. */
  metricOutlier: boolean;
  metricOutlierReason: string;
  status: FeatureRequestStatus;
  /** Non-empty while status is blocked (R5.18). */
  blockedReason: FeatureRequestBlockedReason | '';
  blockedNote: string;
  blockedLink: string;
  /** The surviving ticket while status is duplicate (R6.1). */
  duplicateOfRequestId: number | null;
  percentDone: number | null;
  notes: string;
  blockers: string;
  owner: string;
  engOwner: string;
  ownerUserId: number | null;
  engOwnerUserId: number | null;
  prdPath: string;
  prdUrl: string;
  prdMarkdown: string | null;
  promotedTo: string;
  promotedToFeatureId: number | null;
  /** Program milestone this ticket is committed to (one per ticket). */
  milestoneId: number | null;
  dependsOn: string[];
  createdByUserId: number | null;
  createdByEmail: string;
  createdByName: string;
  createdVia: FeatureRequestCreatedVia;
  createdAt: string;
  updatedAt: string;
  /** Present on authenticated list/detail responses. */
  viewerHasVoted?: boolean;
  /** Points in the viewer's own vote. Absent or null when they have not voted. */
  viewerVoteWeight?: number | null;
  viewerIsWatching?: boolean;
}

/** Field-level delta attached to update activity (not a full version history). */
export interface RoadmapActivityChange {
  field: string;
  from: string;
  to: string;
}

export interface RoadmapActivity {
  id: number;
  entityType: RoadmapCommentEntityType;
  entityId: number;
  activityType: string;
  summary: string;
  actorUserId: number;
  actorEmail: string;
  actorName: string;
  meta?: { changes?: RoadmapActivityChange[] } & Record<string, unknown>;
  createdAt: string;
  /** Present on the global feed so a row can name and link its ticket. */
  entityLabel?: string;
  linkedRequestId?: number | null;
}

export interface SsiDirectoryEntry {
  userId: number;
  email: string;
  name: string;
  role: string;
}

/**
 * WS1 person record (spec R8.1) — the one source of truth for how a person
 * renders, synced server-side from the SSI directory. `email` is the only
 * address notifications may use (R8.8). Inactive people are kept so
 * historical comments/activity still render the right name.
 */
export interface RoadmapPerson {
  userId: number;
  displayName: string;
  username: string;
  email: string;
  role: string;
  active: boolean;
  avatarUrl: string;
  notifyOptOut: boolean;
}

export interface RoadmapTicketNotification {
  id: number;
  trigger: string;
  channel: string;
  recipientUserId: number;
  recipientName: string;
  status: string;
  error: string;
  createdAt: string;
  sentAt: string | null;
}

export interface RoadmapComment {
  id: number;
  entityType: RoadmapCommentEntityType;
  entityId: number;
  body: string;
  authorUserId: number;
  authorEmail: string;
  authorName: string;
  createdAt: string;
  editedAt: string | null;
}

/** A comment that @mentioned the signed-in user, with a link back to the ticket. */
export interface RoadmapMention {
  id: number;
  entityType: RoadmapCommentEntityType;
  entityId: number;
  body: string;
  authorUserId: number;
  authorEmail: string;
  authorName: string;
  createdAt: string;
  editedAt: string | null;
  entityLabel: string;
  linkedRequestId: number | null;
}

export interface RoadmapAttachment {
  id: number;
  entityType: RoadmapAttachmentEntityType;
  entityId: number;
  fileName: string;
  mimeType: string;
  byteSize: number;
  caption: string;
  uploadedByUserId: number;
  createdAt: string;
  /** Short-lived signed URL for preview/download. */
  url: string;
}

export interface Paged<T> {
  items: T[];
  page: number;
  limit: number;
  total: number;
}

// ── Sprints (WS4 — §01–§03) ──────────────────────────────────────────────────

/** Derived from today vs the date range (R3.7) — never stored. */
export const SPRINT_STATES = ['planned', 'active', 'closed'] as const;
export type SprintState = (typeof SPRINT_STATES)[number];

/** '' = live commitment; 'carried'/'returned' = resolved at carry-over or close. */
export type SprintItemResolution = '' | 'carried' | 'returned';

export interface RoadmapSprint {
  id: number;
  name: string;
  /** Inclusive YYYY-MM-DD range. */
  startDate: string;
  endDate: string;
  /** Cadence snapshot at creation — later setting changes don't move it. */
  cadenceWeeks: number;
  origin: TicketOrigin;
  createdByUserId: number | null;
  createdByEmail: string;
  createdAt: string;
  updatedAt: string;
  state: SprintState;
  /** "Day N of M" over working days Mon–Fri (O3). */
  workingDay: { day: number; of: number };
  /** Set once the close flow or the hourly sweep closed it. */
  closedAt: string | null;
  /** True when the sweep (not a person) closed it. */
  autoClosed: boolean;
}

/** List-endpoint sprint: includes its commitments for board joins. */
export interface RoadmapSprintListItem extends RoadmapSprint {
  items: Array<{
    requestId: number;
    carried: boolean;
    resolution: SprintItemResolution;
    /** Auto-carried in; a Sprint Admin still owes "why didn't it close". */
    reasonPending: boolean;
  }>;
}

export interface SprintItem {
  id: number;
  sprintId: number;
  requestId: number;
  /** True when this commitment arrived via carry-over (R2.5). */
  carried: boolean;
  carriedFromSprintId: number | null;
  resolution: SprintItemResolution;
  resolvedAt: string | null;
  /** Why it entered this sprint. '' on rows older than move reasons. */
  moveReason: string;
  /** Why it left (carried / returned). */
  resolutionReason: string;
  reasonPending: boolean;
  /** Confirmed finished in this sprint with a late status update. */
  finishedInSprint: boolean;
  addedByUserId: number | null;
  addedByEmail: string;
  createdAt: string;
  updatedAt: string;
}

export interface SprintDetail {
  sprint: RoadmapSprint;
  items: SprintItem[];
}

export interface CreateSprintInput {
  /** Empty/omitted auto-names server-side ("Sprint 2026-36", R3.6). */
  name?: string;
  startDate: string;
  endDate: string;
  origin: TicketOrigin;
}

export type UpdateSprintInput = Partial<{
  name: string;
  startDate: string;
  endDate: string;
  origin: TicketOrigin;
}>;

export interface CloseSprintDecision {
  requestId: number;
  action: 'carry' | 'backlog';
  /** Required when action = 'carry'. */
  targetSprintId?: number;
  /** Why it did not finish in this sprint. */
  reason: string;
}

/** Sprint Admin-tunable settings rows (sprint.cadence_weeks, score constants…). */
export interface RoadmapSetting {
  key: string;
  value: unknown;
  description: string;
  updatedByUserId: number | null;
  updatedByEmail: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubmitRequestInput {
  name: string;
  /** Required at intake — the server rejects a create without one. */
  ticketType: FeatureRequestType;
  origin: TicketOrigin;
  description?: string;
  source?: 'user' | 'client' | 'internal' | 'management';
  requestedBy?: string;
  category?: string;
  /**
   * WS2 structured category. Single-home sub-areas derive the area; two-home
   * ones require areaId; Other = categoryOtherNote without a subareaId.
   */
  subareaId?: number;
  areaId?: number;
  categoryOtherNote?: string;
  tagIds?: number[];
  /** Submitters set impact, not priority (R5.13); required on bugs (R5.15). */
  clientImpact?: FeatureRequestImpact;
  /** Optional. Same tiers as client impact, about billed work. */
  revenueImpact?: FeatureRequestImpact;
  notes?: string;
  blockers?: string;
  goalDate?: string | null;
  prdMarkdown?: string | null;
}

export type TriageRequestInput = Partial<
  Pick<
    FeatureRequest,
    | 'name'
    | 'description'
    | 'ticketType'
    | 'origin'
    | 'requestedBy'
    | 'category'
    | 'source'
    | 'priority'
    | 'effortShirt'
    | 'effort'
    | 'clientImpact'
    | 'revenueImpact'
    | 'goalDate'
    | 'milestoneId'
    | 'rank'
    | 'status'
    | 'percentDone'
    | 'notes'
    | 'blockers'
    | 'owner'
    | 'engOwner'
    | 'ownerUserId'
    | 'engOwnerUserId'
    | 'prdUrl'
    | 'prdMarkdown'
    | 'dependsOn'
    | 'metricOutlier'
    | 'metricOutlierReason'
  >
> & {
  /** Append-only notes (preferred over replacing `notes`). */
  appendNotes?: string;
  /** Append-only blockers. */
  appendBlockers?: string;
  /** WS2 recategorization (R5.8/R5.11). */
  subareaId?: number | null;
  areaId?: number | null;
  categoryOtherNote?: string;
  /** Full replacement of the ticket's tag set (R5.6). */
  tagIds?: number[];
  /** Required when status enters blocked (R5.18). */
  blockedReason?: FeatureRequestBlockedReason | '';
  blockedNote?: string;
  blockedLink?: string;
  /** Required when status enters duplicate (R6.1). */
  duplicateOfRequestId?: number | null;
  /** Required when leaving complete (R5.21); logged, not stored. */
  reopenReason?: string;
};

export type UpdateFeatureInput = Partial<
  Pick<
    RoadmapFeature,
    | 'groupName'
    | 'module'
    | 'targetDate'
    | 'deliveredDate'
    | 'sortOrder'
  >
>;

/** FR id label, e.g. 33 → "FR-033". */
export function formatFrId(frNumber: number): string {
  return `FR-${String(frNumber).padStart(3, '0')}`;
}

/** FR-375 — ticket URLs use `fr_number`, not the row id. */
export function requestDetailPath(frNumber: number): string {
  return `/admin/roadmap/requests/${frNumber}`;
}

const ROADMAP_LIST_LIMIT = 100;
/**
 * Safety stop for the page-walking readers below. The server clamps `limit` at
 * 100 (architecture.mdc §3.1), so a full list is assembled from N pages — this
 * bounds the walk if `total` is ever larger than the data actually returned.
 */
const MAX_LIST_PAGES = 25;

type ListRequestsParams = {
  status?: FeatureRequestStatus;
  sort?: string;
  /** Omitted returns every origin. The admin board omits it. */
  origin?: TicketOrigin;
};

function requestsPageParams(params: ListRequestsParams, page: number): Record<string, unknown> {
  return {
    page,
    limit: ROADMAP_LIST_LIMIT,
    ...(params.status ? { 'filter[status]': params.status } : {}),
    ...(params.origin ? { 'filter[origin]': params.origin } : {}),
    ...(params.sort ? { sort: params.sort } : {}),
  };
}

/**
 * Walk every page of a paginated roadmap list endpoint and concatenate the
 * results. Page 1 is fetched first for its `total`; the rest go out in
 * parallel (`Promise.all` preserves order, and the server sorts with an `id`
 * tiebreak, so offset paging is stable).
 *
 * @param pageParams Builds the query for a given 1-based page number.
 * @returns The full item set, with `total` still reported by the server.
 */
async function fetchAllPages<T>(
  path: string,
  pageParams: (page: number) => Record<string, unknown>,
): Promise<Paged<T>> {
  const first = await apiGet<Paged<T>>(path, pageParams(1));
  const pageSize = first.limit || ROADMAP_LIST_LIMIT;
  const pageCount = Math.min(Math.max(Math.ceil(first.total / pageSize), 1), MAX_LIST_PAGES);
  if (pageCount === 1) return first;

  const rest = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, i) => apiGet<Paged<T>>(path, pageParams(i + 2))),
  );
  return { ...first, items: [...first.items, ...rest.flatMap((p) => p.items)] };
}

/** A ticket on one of the ops lists: still New, still a Now bug, or sitting in a status queue. */
export type SupportOpenItem = {
  id: number;
  frNumber: number;
  name: string;
  ageMs: number;
  /** Flagged outlier — listed, but not in the averages unless included. */
  outlier: boolean;
};

/** A ticket flagged as a data outlier, with why. */
export type SupportOutlier = {
  id: number;
  frNumber: number;
  name: string;
  reason: string;
};

/** Unscoped queue and created → Triaged speed for one kind of ticket. */
export type SupportScopeKind = {
  unscopedCount: number;
  oldestUnscopedMs: number | null;
  triagedCount: number;
  averageToTriagedMs: number | null;
  priorAverageToTriagedMs: number | null;
  unscoped: SupportOpenItem[];
};

/** How fast bugs at priority Now reach QA and Complete. */
export type SupportP1Bugs = {
  openCount: number;
  oldestOpenMs: number | null;
  closedCount: number;
  averageCloseMs: number | null;
  priorAverageCloseMs: number | null;
  qaCount: number;
  averageToQaMs: number | null;
  priorAverageToQaMs: number | null;
  unknownCloseCount: number;
  setAsideCount: number;
  open: SupportOpenItem[];
};

/** Tickets and bugs sitting in one status. Age is the current stay, weekends removed. */
export type SupportQueue = {
  count: number;
  oldestMs: number | null;
  items: SupportOpenItem[];
};

/** One ended sprint: committed tickets that were Complete when it closed. */
export type SupportSprintClose = {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  committed: number;
  closed: number;
};

/** One point on the product-ops trend. Null means nothing finished in that bucket. */
export type SupportTrendPoint = {
  label: string;
  ticketsToTriagedMs: number | null;
  bugsToTriagedMs: number | null;
  nowToQaMs: number | null;
  nowToCloseMs: number | null;
};

/** Rolling window for the product-ops dashboard. */
export type SupportRange = 'all' | '90d' | '30d' | 'week';

/** GET /api/roadmap/ops — private product-ops dashboard. */
export type SupportDashboard = {
  generatedAt: string;
  range: SupportRange;
  scope: {
    tickets: SupportScopeKind;
    bugs: SupportScopeKind;
  };
  p1Bugs: SupportP1Bugs;
  queues: {
    qa: SupportQueue;
    uat: SupportQueue;
    waitingOnSubmitter: SupportQueue;
  };
  sprints: SupportSprintClose[];
  trend: SupportTrendPoint[];
  outliers: {
    /** True when the averages above leave these tickets out. */
    excluded: boolean;
    items: SupportOutlier[];
    /** The four headline averages under the other setting. Null when nothing is flagged. */
    alternate: SupportOutlierAlternate | null;
  };
  flow: OpsFlow;
};

export type SupportOutlierAlternate = {
  ticketsToTriagedMs: number | null;
  bugsToTriagedMs: number | null;
  nowToQaMs: number | null;
  nowToCloseMs: number | null;
};

export const OPS_GROUP_BYS = ['type', 'owner', 'engOwner', 'origin'] as const;
export type OpsGroupBy = (typeof OPS_GROUP_BYS)[number];
export type OpsCount = { key: string; label: string; count: number };
export type OpsBreakdown = Record<OpsGroupBy, OpsCount[]>;

export type OpsTicketRef = {
  id: number;
  frNumber: number;
  name: string;
  ticketType: string;
  owner: string;
  engOwner: string;
  origin: string;
};

export type OpsMoveKind = 'added' | 'removed' | 'carried_out' | 'returned';

export type OpsSprintMove = OpsTicketRef & {
  kind: OpsMoveKind;
  at: string;
  reason: string;
  actorName: string;
  activeSprint: boolean;
};

export type OpsSprintChange = {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  state: 'active' | 'closed';
  plannedAtStart: number;
  addedMidSprint: number;
  removed: number;
  carriedIn: number;
  carriedOut: number;
  returned: number;
  finishedLate: number;
  done: number;
  scopeChangePercent: number | null;
  /** Effort of every ticket that stayed in the sprint, planned or added. */
  committedUnits: number;
  deliveredUnits: number;
  breakdown: { added: OpsBreakdown; removed: OpsBreakdown; carriedOut: OpsBreakdown };
  moves: OpsSprintMove[];
};

export type OpsStuckTicket = OpsTicketRef & {
  status: string;
  carryCount: number;
  currentSprintName: string | null;
  reasons: string[];
  ageMs: number;
};

/** Every live ticket flagged as a data outlier, any type. */
export type OpsOutlier = OpsTicketRef & {
  status: string;
  reason: string;
  updatedAt: string;
};

export type OpsPerson = {
  key: string;
  name: string;
  open: number;
  closed: number;
  averageCloseMs: number | null;
  carriedOut: number;
  inQa: number;
  inUat: number;
};

export type OpsStage =
  | 'new'
  | 'triaged'
  | 'planning'
  | 'development'
  | 'blocked'
  | 'qa'
  | 'acceptance_testing'
  | 'ready_for_release'
  | 'waiting_on_submitter';

export type OpsStageGroup = {
  key: 'bug' | 'feature' | 'other';
  label: string;
  completed: number;
  stages: Array<{ stage: OpsStage; averageMs: number | null; count: number }>;
};

export type OpsFlow = {
  sprints: OpsSprintChange[];
  stuck: OpsStuckTicket[];
  outliers: OpsOutlier[];
  people: OpsPerson[];
  stages: {
    groups: OpsStageGroup[];
    qaReached: number;
    sentBack: number;
    sentBackPercent: number | null;
  };
  reasonsOwed: {
    pending: Array<OpsTicketRef & { sprintId: number; sprintName: string }>;
    movesWithoutReason: number;
  };
  logged: { total: number; complete: number; byType: OpsCount[] };
  motion: { enteredQa: number; enteredUat: number; triaged: number };
};

export const roadmapApi = {
  /** Every board feature — page-walked so the board never truncates at 100. */
  listFeatures(): Promise<Paged<RoadmapFeature>> {
    return fetchAllPages<RoadmapFeature>('/roadmap/features', (page) => ({
      page,
      limit: ROADMAP_LIST_LIMIT,
    }));
  },
  /** One board feature with every tracker step — the ticket page's parent lookup. */
  getFeature(id: number): Promise<RoadmapFeature> {
    return apiGet<RoadmapFeature>(`/roadmap/features/${id}`);
  },
  updateFeature(id: number, patch: UpdateFeatureInput): Promise<RoadmapFeature> {
    return apiPatch<RoadmapFeature>(`/roadmap/features/${id}`, patch);
  },
  /** Soft-delete (archive) a board feature; every step returns to triaged. */
  deleteFeature(
    id: number,
  ): Promise<{ id: number; unlinkedRequestId: number | null; unlinkedRequestIds: number[] }> {
    return apiDelete<{
      id: number;
      unlinkedRequestId: number | null;
      unlinkedRequestIds: number[];
    }>(`/roadmap/features/${id}`);
  },
  /** Create a NEW ticket as a tracker step on an existing feature (always creates). */
  createFeatureTicket(
    id: number,
    input: CreateFeatureTicketInput,
  ): Promise<{ feature: RoadmapFeature; request: FeatureRequest }> {
    return apiPost<{ feature: RoadmapFeature; request: FeatureRequest }>(
      `/roadmap/features/${id}/tickets`,
      input,
    );
  },
  /** Attach an open ticket as a step; 409 when it already belongs to a feature. */
  attachFeatureTicket(
    id: number,
    input: AttachFeatureTicketInput,
  ): Promise<{ feature: RoadmapFeature; request: FeatureRequest }> {
    return apiPost<{ feature: RoadmapFeature; request: FeatureRequest }>(
      `/roadmap/features/${id}/tickets/attach`,
      input,
    );
  },
  /** Unlink one step; the ticket goes back to triaged and the feature stays. */
  unlinkFeatureTicket(
    id: number,
    requestId: number,
  ): Promise<{ feature: RoadmapFeature; request: FeatureRequest }> {
    return apiDelete<{ feature: RoadmapFeature; request: FeatureRequest }>(
      `/roadmap/features/${id}/tickets/${requestId}`,
    );
  },
  /** Full left-to-right step order — every linked ticket id exactly once. */
  reorderFeatureTickets(id: number, requestIds: number[]): Promise<RoadmapFeature> {
    return apiPatch<RoadmapFeature>(`/roadmap/features/${id}/tickets/order`, { requestIds });
  },
  /**
   * Create a ticket for a board-only feature and link both sides.
   * Idempotent when a linked ticket already exists.
   */
  ensureTicketForFeature(
    id: number,
  ): Promise<{ feature: RoadmapFeature; request: FeatureRequest; created: boolean }> {
    return apiPost<{ feature: RoadmapFeature; request: FeatureRequest; created: boolean }>(
      `/roadmap/features/${id}/ensure-ticket`,
      {},
    );
  },

  listMilestones(origin?: TicketOrigin): Promise<Paged<RoadmapMilestone>> {
    return fetchAllPages<RoadmapMilestone>('/roadmap/milestones', (page) => ({
      page,
      limit: ROADMAP_LIST_LIMIT,
      ...(origin ? { 'filter[origin]': origin } : {}),
    }));
  },
  /** One milestone with every committed ticket, its status, and its live sprint. */
  getMilestone(id: number): Promise<RoadmapMilestone> {
    return apiGet<RoadmapMilestone>(`/roadmap/milestones/${id}`);
  },
  createMilestone(input: CreateMilestoneInput): Promise<RoadmapMilestone> {
    return apiPost<RoadmapMilestone>('/roadmap/milestones', input);
  },
  /** Create a NEW ticket / bug / feedback already committed to a milestone. */
  createMilestoneTicket(
    id: number,
    input: CreateMilestoneTicketInput,
  ): Promise<{ milestone: RoadmapMilestone; request: FeatureRequest }> {
    return apiPost<{ milestone: RoadmapMilestone; request: FeatureRequest }>(
      `/roadmap/milestones/${id}/tickets`,
      input,
    );
  },
  /** Commit an open ticket; 409 when it already belongs to a milestone. */
  attachMilestoneTicket(
    id: number,
    input: AttachMilestoneTicketInput,
  ): Promise<{ milestone: RoadmapMilestone; request: FeatureRequest }> {
    return apiPost<{ milestone: RoadmapMilestone; request: FeatureRequest }>(
      `/roadmap/milestones/${id}/tickets/attach`,
      input,
    );
  },
  /** Take one ticket off a milestone; the ticket is otherwise untouched. */
  removeMilestoneTicket(
    id: number,
    requestId: number,
  ): Promise<{ milestone: RoadmapMilestone; request: FeatureRequest }> {
    return apiDelete<{ milestone: RoadmapMilestone; request: FeatureRequest }>(
      `/roadmap/milestones/${id}/tickets/${requestId}`,
    );
  },
  /** Full panel order — every committed ticket id exactly once. */
  reorderMilestoneTickets(id: number, requestIds: number[]): Promise<RoadmapMilestone> {
    return apiPatch<RoadmapMilestone>(`/roadmap/milestones/${id}/tickets/order`, { requestIds });
  },
  updateMilestone(id: number, patch: UpdateMilestoneInput): Promise<RoadmapMilestone> {
    return apiPatch<RoadmapMilestone>(`/roadmap/milestones/${id}`, patch);
  },
  /** Soft-delete (archive) a milestone. */
  deleteMilestone(id: number): Promise<{ id: number }> {
    return apiDelete<{ id: number }>(`/roadmap/milestones/${id}`);
  },

  /**
   * Every ticket matching the filter, assembled across pages. The Tickets table
   * sorts, searches, and scores over the whole backlog, so a single capped page
   * would silently hide rows once the backlog passes `ROADMAP_LIST_LIMIT`.
   */
  listRequests(params: ListRequestsParams = {}): Promise<Paged<FeatureRequest>> {
    return fetchAllPages<FeatureRequest>('/roadmap/requests', (page) =>
      requestsPageParams(params, page),
    );
  },
  getRequest(id: number): Promise<FeatureRequest> {
    return apiGet<FeatureRequest>(`/roadmap/requests/${id}`);
  },
  submitRequest(input: SubmitRequestInput): Promise<FeatureRequest> {
    return apiPost<FeatureRequest>('/roadmap/requests', input);
  },
  triageRequest(id: number, patch: TriageRequestInput): Promise<FeatureRequest> {
    return apiPatch<FeatureRequest>(`/roadmap/requests/${id}`, patch);
  },
  promoteRequest(
    id: number,
    overrides: { groupName?: string; slug?: string; module?: string; effort?: RoadmapEffort } = {},
  ): Promise<{ feature: RoadmapFeature; request: FeatureRequest }> {
    return apiPost<{ feature: RoadmapFeature; request: FeatureRequest }>(
      `/roadmap/requests/${id}/promote`,
      overrides,
    );
  },
  /** `featureArchived` is false when other steps kept the board card. */
  demoteRequest(
    id: number,
  ): Promise<{ request: FeatureRequest; featureId: number | null; featureArchived: boolean }> {
    return apiPost<{ request: FeatureRequest; featureId: number | null; featureArchived: boolean }>(
      `/roadmap/requests/${id}/demote`,
      {},
    );
  },
  /** Soft-delete (archive) a ticket; also archives a linked board feature. */
  deleteRequest(id: number): Promise<{ id: number; deletedFeatureId: number | null }> {
    return apiDelete<{ id: number; deletedFeatureId: number | null }>(`/roadmap/requests/${id}`);
  },
  /** `voteWeight` is the points this toggle added or took back. Super voters pass 1 or 5. */
  toggleRequestVote(
    id: number,
    weight?: 1 | 5,
  ): Promise<FeatureRequest & { voteWeight?: number }> {
    return apiPost<FeatureRequest & { voteWeight?: number }>(
      `/roadmap/requests/${id}/vote`,
      weight == null ? {} : { weight },
    );
  },

  listComments(
    entityType: RoadmapCommentEntityType,
    entityId: number,
  ): Promise<{ items: RoadmapComment[] }> {
    return apiGet<{ items: RoadmapComment[] }>('/roadmap/comments', { entityType, entityId });
  },
  /** Comments that @mention the signed-in SSI user. */
  listMentions(): Promise<{ items: RoadmapMention[] }> {
    return apiGet<{ items: RoadmapMention[] }>('/roadmap/mentions');
  },
  addComment(
    entityType: RoadmapCommentEntityType,
    entityId: number,
    body: string,
  ): Promise<RoadmapComment> {
    return apiPost<RoadmapComment>('/roadmap/comments', { entityType, entityId, body });
  },
  updateComment(id: number, body: string): Promise<RoadmapComment> {
    return apiPatch<RoadmapComment>(`/roadmap/comments/${id}`, { body });
  },
  deleteComment(id: number): Promise<{ id: number }> {
    return apiDelete<{ id: number }>(`/roadmap/comments/${id}`);
  },

  listAttachments(
    entityType: RoadmapAttachmentEntityType,
    entityId: number,
  ): Promise<{ items: RoadmapAttachment[] }> {
    return apiGet<{ items: RoadmapAttachment[] }>('/roadmap/attachments', {
      entityType,
      entityId,
    });
  },
  uploadAttachment(
    entityType: RoadmapAttachmentEntityType,
    entityId: number,
    file: File,
    caption?: string,
  ): Promise<RoadmapAttachment> {
    const form = new FormData();
    form.append('file', file);
    form.append('entityType', entityType);
    form.append('entityId', String(entityId));
    if (caption) form.append('caption', caption);
    return apiPostMultipart<RoadmapAttachment>('/roadmap/attachments', form);
  },
  updateAttachmentCaption(id: number, caption: string): Promise<RoadmapAttachment> {
    return apiPatch<RoadmapAttachment>(`/roadmap/attachments/${id}`, { caption });
  },
  deleteAttachment(id: number): Promise<{ id: number }> {
    return apiDelete<{ id: number }>(`/roadmap/attachments/${id}`);
  },

  listSsiDirectory(q?: string): Promise<{ items: SsiDirectoryEntry[] }> {
    return apiGet<{ items: SsiDirectoryEntry[] }>('/roadmap/ssi-directory', q ? { q } : {});
  },
  /** The whole category taxonomy — small (9/41/4 rows), cached for an hour. */
  getTaxonomy(includeInactive = false): Promise<RoadmapTaxonomy> {
    return apiGet<RoadmapTaxonomy>(
      '/roadmap/taxonomy',
      includeInactive ? { includeInactive: '1' } : {},
    );
  },
  /** The viewer's roadmap roles (sprint_admin / triage) — gates admin UI. */
  getMyRoadmapRoles(): Promise<{ roles: string[]; canSuperVote?: boolean }> {
    return apiGet<{ roles: string[]; canSuperVote?: boolean }>('/roadmap/roles/me');
  },
  createArea(input: { name: string; sort?: number }): Promise<TicketArea> {
    return apiPost<TicketArea>('/roadmap/taxonomy/areas', input);
  },
  updateArea(
    id: number,
    patch: Partial<Pick<TicketArea, 'name' | 'sort' | 'active' | 'replacedByAreaId'>>,
  ): Promise<TicketArea> {
    return apiPatch<TicketArea>(`/roadmap/taxonomy/areas/${id}`, patch);
  },
  createSubarea(input: {
    areaId: number;
    secondAreaId?: number | null;
    name: string;
    hint?: string;
    sort?: number;
  }): Promise<TicketSubarea> {
    return apiPost<TicketSubarea>('/roadmap/taxonomy/subareas', input);
  },
  updateSubarea(
    id: number,
    patch: Partial<
      Pick<
        TicketSubarea,
        'areaId' | 'secondAreaId' | 'name' | 'hint' | 'sort' | 'active' | 'replacedBySubareaId'
      >
    >,
  ): Promise<TicketSubarea> {
    return apiPatch<TicketSubarea>(`/roadmap/taxonomy/subareas/${id}`, patch);
  },
  createTag(input: { name: string; sort?: number }): Promise<TicketTag> {
    return apiPost<TicketTag>('/roadmap/taxonomy/tags', input);
  },
  updateTag(
    id: number,
    patch: Partial<Pick<TicketTag, 'name' | 'sort' | 'active'>>,
  ): Promise<TicketTag> {
    return apiPatch<TicketTag>(`/roadmap/taxonomy/tags/${id}`, patch);
  },
  /** Full roster including inactive people — chips filter by `active` as needed. */
  listPeople(): Promise<{ items: RoadmapPerson[] }> {
    return apiGet<{ items: RoadmapPerson[] }>('/roadmap/people', { includeInactive: '1' });
  },
  setMyNotifyOptOut(notifyOptOut: boolean): Promise<RoadmapPerson> {
    return apiPatch<RoadmapPerson>('/roadmap/people/me', { notifyOptOut });
  },
  listRequestNotifications(requestId: number): Promise<{ items: RoadmapTicketNotification[] }> {
    return apiGet<{ items: RoadmapTicketNotification[] }>(
      `/roadmap/requests/${requestId}/notifications`,
    );
  },
  listActivity(
    entityType: RoadmapCommentEntityType,
    entityId: number,
  ): Promise<{ items: RoadmapActivity[] }> {
    return apiGet<{ items: RoadmapActivity[] }>('/roadmap/activity', { entityType, entityId });
  },
  listActivityFeed(input?: {
    entityType?: RoadmapCommentEntityType;
    limit?: number;
  }): Promise<{ items: RoadmapActivity[] }> {
    return apiGet<{ items: RoadmapActivity[] }>('/roadmap/activity', input ?? {});
  },
  // ── Sprints (WS4) ──────────────────────────────────────────────────────────
  listSprints(origin?: TicketOrigin): Promise<{ items: RoadmapSprintListItem[] }> {
    return apiGet<{ items: RoadmapSprintListItem[] }>(
      '/roadmap/sprints',
      origin ? { 'filter[origin]': origin } : {},
    );
  },
  getSprint(id: number): Promise<SprintDetail> {
    return apiGet<SprintDetail>(`/roadmap/sprints/${id}`);
  },
  createSprint(input: CreateSprintInput): Promise<RoadmapSprint> {
    return apiPost<RoadmapSprint>('/roadmap/sprints', input);
  },
  updateSprint(id: number, patch: UpdateSprintInput): Promise<RoadmapSprint> {
    return apiPatch<RoadmapSprint>(`/roadmap/sprints/${id}`, patch);
  },
  /** Planned sprints only — items return to the backlog with it. A reason is required when it has items. */
  deleteSprint(id: number, reason?: string): Promise<{ deleted: true }> {
    return apiDelete<{ deleted: true }>(`/roadmap/sprints/${id}`, {
      data: reason ? { reason } : {},
    });
  },
  /**
   * Commit one of the caller's assigned backlog tickets to the current sprint.
   * Any SSI employee can do this for their own ticket.
   */
  pullMyTicket(requestId: number, reason: string): Promise<{ sprintId: number; sprintName: string; added: number }> {
    return apiPost<{ sprintId: number; sprintName: string; added: number }>('/roadmap/sprints/mine', {
      requestId,
      reason,
    });
  },
  addSprintItems(
    id: number,
    requestIds: number[],
    reason: string,
  ): Promise<{ added: number; items: SprintItem[] }> {
    return apiPost<{ added: number; items: SprintItem[] }>(`/roadmap/sprints/${id}/items`, {
      requestIds,
      reason,
    });
  },
  removeSprintItem(id: number, requestId: number, reason: string): Promise<{ removed: true }> {
    return apiDelete<{ removed: true }>(`/roadmap/sprints/${id}/items/${requestId}`, {
      data: { reason },
    });
  },
  carrySprintItem(
    id: number,
    requestId: number,
    targetSprintId: number,
    reason: string,
  ): Promise<{ carried: true }> {
    return apiPost<{ carried: true }>(`/roadmap/sprints/${id}/items/${requestId}/carry`, {
      targetSprintId,
      reason,
    });
  },
  /** Answer "why didn't this close last sprint" for a carried-in ticket. */
  explainCarriedItem(id: number, requestId: number, reason: string): Promise<SprintItem> {
    return apiPost<SprintItem>(`/roadmap/sprints/${id}/items/${requestId}/explain`, { reason });
  },
  /** It finished in the sprint it came from — mark Complete and undo the carry. */
  markFinishedInPreviousSprint(id: number, requestId: number): Promise<{ finished: true }> {
    return apiPost<{ finished: true }>(
      `/roadmap/sprints/${id}/items/${requestId}/finished-in-previous`,
      {},
    );
  },
  /** Close flow (R3.8): one decision per unfinished item. */
  closeSprint(id: number, decisions: CloseSprintDecision[]): Promise<SprintDetail> {
    return apiPost<SprintDetail>(`/roadmap/sprints/${id}/close`, { decisions });
  },
  /** Sprint Admin-tunable settings — readable by any SSI role. */
  listSettings(): Promise<{ items: RoadmapSetting[] }> {
    return apiGet<{ items: RoadmapSetting[] }>('/roadmap/settings');
  },
  updateSetting(key: string, value: unknown): Promise<RoadmapSetting> {
    return apiPatch<RoadmapSetting>(`/roadmap/settings/${key}`, { value });
  },

  getWatchState(
    entityType: RoadmapCommentEntityType,
    entityId: number,
  ): Promise<{ watching: boolean }> {
    return apiGet<{ watching: boolean }>('/roadmap/watch', { entityType, entityId });
  },
  watch(
    entityType: RoadmapCommentEntityType,
    entityId: number,
  ): Promise<{ watching: true }> {
    return apiPost<{ watching: true }>('/roadmap/watch', { entityType, entityId });
  },
  unwatch(
    entityType: RoadmapCommentEntityType,
    entityId: number,
  ): Promise<{ watching: false }> {
    return apiPost<{ watching: false }>('/roadmap/unwatch', { entityType, entityId });
  },
  /**
   * Product-ops dashboard. The server allows a short email allowlist; a 403
   * is the expected answer for everyone else, so it does not toast.
   */
  getSupportDashboard(
    range: SupportRange = 'all',
    includeOutliers = false,
  ): Promise<SupportDashboard> {
    const params = includeOutliers ? { range, includeOutliers: '1' } : { range };
    return apiGet<SupportDashboard>('/roadmap/ops', params, {
      _suppressPermissionToast: true,
    });
  },
};
